import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import { CardsVariant } from '@/components/page-sections/review-widget/variants/cards';
import { GridVariant } from '@/components/page-sections/review-widget/variants/grid';
import { ListVariant } from '@/components/page-sections/review-widget/variants/list';
import type { ReviewDisplaySettings } from '@/components/page-sections/review-widget/variants/review-display';
import { EmptyState } from '@/components/shared/empty-state';
import { IndexPagination } from '@/components/shared/index-pagination';
import type { ReviewRecord } from '@/lib/blocks/collection/records';
import { readRecord, readString } from '@/lib/prop-utils';
import type { PaginationInfo } from '@/types/pagination';
import { TEMPLATE_PREVIEW_REVIEWS } from './review-index-preview-data';

function readReviews(value: unknown): ReviewRecord[] {
  return Array.isArray(value) ? (value as ReviewRecord[]) : [];
}

function readPagination(value: unknown): PaginationInfo | undefined {
  const record = readRecord(value);
  if (!record) return undefined;
  if (typeof record.currentPage !== 'number' || typeof record.totalPages !== 'number') {
    return undefined;
  }

  return record as unknown as PaginationInfo;
}
export function ReviewIndexBlock({ props }: { props: Record<string, unknown> }) {
  const sectionContext = usePageSectionContext();
  const variant = sectionContext?.variant ?? 'grid';
  const isStaticPreview = sectionContext ? sectionContext.renderMode !== 'default' : false;

  const title = readString(props.title) ?? undefined;
  const resolvedReviews = readReviews(props.items);
  const reviews = isStaticPreview ? TEMPLATE_PREVIEW_REVIEWS : resolvedReviews;
  const pagination = readPagination(props.pagination);
  const settings = readRecord(props.display) as ReviewDisplaySettings | undefined;

  if (reviews.length === 0) {
    return <EmptyState message="No reviews available. Add reviews to display them here." />;
  }

  return (
    <>
      {variant === 'list' ? (
        <ListVariant title={title} reviews={reviews} settings={settings} />
      ) : variant === 'cards' ? (
        <CardsVariant title={title} reviews={reviews} settings={settings} hideBackground />
      ) : (
        <GridVariant title={title} reviews={reviews} settings={settings} />
      )}
      {pagination ? <IndexPagination pagination={pagination} /> : null}
    </>
  );
}
